// Reading an .xlsx: the file's own vocabulary, one level up from bytes.
//
// This layer is deliberately FAITHFUL rather than opinionated — it reports what
// the file says (a number-format CODE, an Excel serial, a formula string) and
// leaves every judgement call about how that becomes a gTables cell to
// data/xlsxImport.ts. Splitting it that way is what makes the mapping testable
// without a zip in the test.
//
// What is read: worksheets in workbook order, values, formulas, number formats,
// bold/italic/colour/fill/borders/alignment, column widths, row heights, hidden
// bands and frozen panes. What is not: charts, images, pivot tables, defined
// names, data validation, conditional formatting and comments — every one of
// which is a feature gTables would have to have first.

import type { Border, BorderEdge, CellStyle, CellValue } from '@/model/types';
import { parseCellA1, parseRange, translateFormula } from '@/model/address';
import type { Merge } from '@/model/types';
import { openZip, type Bytes, type ZipArchive } from './zip';
import { scanXml, type Attrs } from './xml';

export interface XlsxCell {
  row: number;
  col: number;
  /**
   * What the file states, converted only as far as its declared type demands:
   * a shared-string index becomes its string, `b` becomes a boolean, and a
   * number stays a number — a date is still the serial Excel stored.
   *
   * For a formula cell this is the value Excel last CALCULATED. It is not
   * redundant: it is the fallback when the formula cannot be re-run here.
   */
  value: CellValue;
  /** Formula text without the leading `=`. */
  formula?: string;
  /** The number-format code, e.g. `#,##0.00`. Absent means General. */
  numFmt?: string;
  style?: CellStyle;
}

export interface XlsxSheet {
  name: string;
  /** Hidden in Excel's own tab bar. */
  hidden: boolean;
  cells: XlsxCell[];
  /** Only the widths and heights the user actually set, in pixels. */
  colWidths: Record<number, number>;
  rowHeights: Record<number, number>;
  hiddenRows: Record<number, true>;
  hiddenCols: Record<number, true>;
  freezeRows: number;
  freezeCols: number;
  /** Count of `<mergeCell>` elements seen, including any that failed to parse. */
  merged: number;
  /** The merged ranges themselves, successfully parsed. */
  merges: Merge[];
  rows: number;
  cols: number;
}

export interface XlsxWorkbook {
  sheets: XlsxSheet[];
  /**
   * The 1904 date system — a workbook born on a pre-2011 Mac Excel. Its serials
   * are 1462 days behind everyone else's, and a cashflow that is four years and
   * a day early is the kind of wrong that looks like a typo.
   */
  date1904: boolean;
  /**
   * Excel's own named ranges, as written (`name`) and referenced (`ref`, e.g.
   * `Budget!$B$2:$B$10`) — workbook-scoped only. Excel's own built-ins (print
   * area, print titles, the filter database — `_xlnm.*`) and sheet-scoped
   * names (`localSheetId` set) are filtered out here: the former are not
   * something a formula references, and the latter can shadow a workbook-
   * scoped name of the same spelling, which this reader has no concept of.
   */
  definedNames: { name: string; ref: string }[];
}

/**
 * The OLE2 compound-document signature that starts a pre-2007 `.xls`.
 *
 * Worth recognising even though nothing here can read one: an .xls is a binary
 * record stream inside a little filesystem, sharing nothing with .xlsx but the
 * icon, and "that file could not be read" sends someone hunting for a fault in
 * a file that is perfectly good. Knowing what it IS turns the dead end into an
 * instruction — open it and Save As .xlsx.
 */
export function looksLikeLegacyXls(data: Uint8Array): boolean {
  const magic = [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1];
  if (data.length < magic.length) return false;
  return magic.every((byte, i) => data[i] === byte);
}

export async function readXlsx(data: Bytes): Promise<XlsxWorkbook> {
  const zip = openZip(data);
  const workbookPart = findWorkbookPart(zip);
  if (!workbookPart) {
    throw new Error('That file is a zip, but not an Excel workbook — no xl/workbook.xml inside.');
  }

  const xml = (await zip.text(workbookPart)) ?? '';
  const { sheets: refs, date1904, definedNames } = parseWorkbook(xml);
  const rels = parseRels((await zip.text(relsPathFor(workbookPart))) ?? '');
  const dir = workbookPart.slice(0, workbookPart.lastIndexOf('/') + 1);

  const strings = parseSharedStrings((await zip.text(`${dir}sharedStrings.xml`)) ?? '');
  const styles = parseStyles((await zip.text(`${dir}styles.xml`)) ?? '');

  const sheets: XlsxSheet[] = [];
  for (const ref of refs) {
    const target = ref.rid ? rels.get(ref.rid) : undefined;
    const path = target ? resolvePart(dir, target) : undefined;
    const sheetXml = path ? await zip.text(path) : null;
    // A worksheet the package promises but does not contain is not a reason to
    // refuse the other nine; it comes through as an empty tab.
    sheets.push(parseWorksheet(sheetXml ?? '', ref.name, ref.hidden, strings, styles));
  }
  return { sheets, date1904, definedNames };
}

// ---------------------------------------------------------------------------
// Package plumbing
// ---------------------------------------------------------------------------

/**
 * `xl/workbook.xml` in everything Excel writes, but the part name is nominally
 * free — so the usual place is tried first and the package is searched second.
 */
function findWorkbookPart(zip: ZipArchive): string | null {
  if (zip.has('xl/workbook.xml')) return 'xl/workbook.xml';
  return zip.names().find((n) => /(^|\/)workbook\.xml$/.test(n)) ?? null;
}

function relsPathFor(part: string): string {
  const slash = part.lastIndexOf('/');
  return `${part.slice(0, slash + 1)}_rels/${part.slice(slash + 1)}.rels`;
}

/** Relationship targets are relative to the part's own folder, and may climb. */
function resolvePart(dir: string, target: string): string {
  if (target.startsWith('/')) return target.slice(1);
  const out: string[] = [];
  for (const segment of `${dir}${target}`.split('/')) {
    if (segment === '' || segment === '.') continue;
    if (segment === '..') out.pop();
    else out.push(segment);
  }
  return out.join('/');
}

function parseRels(xml: string): Map<string, string> {
  const rels = new Map<string, string>();
  scanXml(xml, {
    open(name, attrs) {
      if (name === 'Relationship' && attrs.Id && attrs.Target) rels.set(attrs.Id, attrs.Target);
    },
  });
  return rels;
}

interface SheetRef {
  name: string;
  rid?: string;
  hidden: boolean;
}

function parseWorkbook(
  xml: string
): { sheets: SheetRef[]; date1904: boolean; definedNames: { name: string; ref: string }[] } {
  const sheets: SheetRef[] = [];
  const definedNames: { name: string; ref: string }[] = [];
  let date1904 = false;
  // Set while inside a <definedName> worth keeping; null while inside one that
  // is not (a built-in or sheet-scoped one) or outside the element entirely.
  let currentName: string | null = null;
  let currentRef = '';
  scanXml(xml, {
    open(name, attrs) {
      if (name === 'workbookPr') {
        date1904 = attrs.date1904 === '1' || attrs.date1904 === 'true';
      } else if (name === 'sheet') {
        sheets.push({
          name: attrs.name ?? `Sheet${sheets.length + 1}`,
          rid: attrs['r:id'] ?? attrs.id,
          hidden: attrs.state === 'hidden' || attrs.state === 'veryHidden',
        });
      } else if (name === 'definedName') {
        const ok = attrs.name && !attrs.name.startsWith('_xlnm.') && attrs.localSheetId === undefined;
        currentName = ok ? attrs.name! : null;
        currentRef = '';
      }
    },
    close(name) {
      if (name === 'definedName' && currentName !== null) {
        definedNames.push({ name: currentName, ref: currentRef });
        currentName = null;
      }
    },
    text(text) {
      if (currentName !== null) currentRef += text;
    },
  });
  return { sheets, date1904, definedNames };
}

/**
 * The shared string table. One `<si>` is one string, but it can arrive as a run
 * of `<r><t>` fragments with different formatting — which is one string as far
 * as a cell is concerned, so the fragments are concatenated. `<rPh>` is a
 * phonetic guide for Japanese text and is NOT part of the string.
 */
function parseSharedStrings(xml: string): string[] {
  const out: string[] = [];
  let current: string | null = null;
  let inText = false;
  let skipDepth = 0;

  scanXml(xml, {
    open(name) {
      if (skipDepth > 0) {
        skipDepth++;
        return;
      }
      if (name === 'rPh') skipDepth = 1;
      else if (name === 'si') current = '';
      else if (name === 't') inText = true;
    },
    close(name) {
      if (skipDepth > 0) {
        skipDepth--;
        return;
      }
      if (name === 't') inText = false;
      else if (name === 'si') {
        out.push(unescapeControlCodes(current ?? ''));
        current = null;
      }
    },
    text(text) {
      if (inText && current !== null) current += text;
    },
  });
  return out;
}

/**
 * Excel escapes characters XML cannot carry as `_x000D_`. Undoing it is not
 * cosmetic: a multi-line label pasted into a cell comes back with the literal
 * text `_x000A_` in the middle of it otherwise.
 */
function unescapeControlCodes(s: string): string {
  if (!s.includes('_x')) return s;
  return s.replace(/_x([0-9a-fA-F]{4})_/g, (all, hex) => {
    const code = parseInt(hex, 16);
    return code === 0 ? all : String.fromCharCode(code);
  });
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

interface Styles {
  /** Number-format code per cell-format index (the `s` attribute of a `<c>`). */
  numFmt: (string | undefined)[];
  /** Everything else about the look, per the same index. */
  style: (CellStyle | undefined)[];
}

/**
 * The formats Excel does not write down, because every reader is assumed to
 * know them. Only the ones that survive the trip into gTables' closed set of
 * formats are listed; the rest fall through to General, which is what an
 * unknown id should look like anyway.
 */
const BUILTIN_FORMATS: Record<number, string> = {
  1: '0',
  2: '0.00',
  3: '#,##0',
  4: '#,##0.00',
  9: '0%',
  10: '0.00%',
  11: '0.00E+00',
  12: '# ?/?',
  13: '# ??/??',
  14: 'mm-dd-yy',
  15: 'd-mmm-yy',
  16: 'd-mmm',
  17: 'mmm-yy',
  18: 'h:mm AM/PM',
  19: 'h:mm:ss AM/PM',
  20: 'h:mm',
  21: 'h:mm:ss',
  22: 'm/d/yy h:mm',
  37: '#,##0 ;(#,##0)',
  38: '#,##0 ;[Red](#,##0)',
  39: '#,##0.00;(#,##0.00)',
  40: '#,##0.00;[Red](#,##0.00)',
  44: '_("$"* #,##0.00_)',
  45: 'mm:ss',
  46: '[h]:mm:ss',
  47: 'mmss.0',
  48: '##0.0E+0',
  49: '@',
};

function parseStyles(xml: string): Styles {
  const codes = new Map<number, string>();
  const fonts: CellStyle[] = [];
  const fills: (string | undefined)[] = [];
  const borders: Partial<Record<BorderEdge, Border>>[] = [];
  const numFmt: (string | undefined)[] = [];
  const style: (CellStyle | undefined)[] = [];

  // Which list the scanner is currently filling. styles.xml reuses `<color>`
  // and `<xf>` in several sections, and the section is the only thing that says
  // what one means — `<xf>` under `<cellStyleXfs>` is a NAMED style, and a cell
  // that pointed at it by index would come back wearing someone else's face.
  let section = '';
  let font: CellStyle = {};
  let fill: string | undefined;
  let border: Partial<Record<BorderEdge, Border>> = {};
  let edge: BorderEdge | null = null;
  let edgeStyle = '';
  let xf: { numFmtId: number; fontId: number; fillId: number; borderId: number; style: CellStyle } | null = null;
  let inPatternFill = false;
  let patternSolid = false;

  scanXml(xml, {
    open(name, attrs) {
      switch (name) {
        case 'numFmts':
        case 'fonts':
        case 'fills':
        case 'borders':
        case 'cellXfs':
        case 'cellStyleXfs':
        case 'dxfs':
          section = name;
          return;
        case 'numFmt':
          if (attrs.numFmtId && attrs.formatCode !== undefined) {
            codes.set(Number(attrs.numFmtId), attrs.formatCode);
          }
          return;
      }

      if (section === 'fonts') {
        if (name === 'font') font = {};
        else if (name === 'b') font.bold = attrs.val !== '0' && attrs.val !== 'false';
        else if (name === 'i') font.italic = attrs.val !== '0' && attrs.val !== 'false';
        else if (name === 'color') {
          const c = colourOf(attrs);
          if (c) font.color = c;
        }
        return;
      }

      if (section === 'fills') {
        if (name === 'fill') fill = undefined;
        else if (name === 'patternFill') {
          inPatternFill = true;
          patternSolid = attrs.patternType === 'solid';
        } else if (name === 'fgColor' && inPatternFill && patternSolid) {
          fill = colourOf(attrs);
        }
        return;
      }

      if (section === 'borders') {
        if (name === 'border') border = {};
        else if (name === 'left' || name === 'right' || name === 'top' || name === 'bottom') {
          edge = name;
          edgeStyle = attrs.style ?? '';
          if (edgeStyle) border[edge] = { w: borderWidth(edgeStyle), c: '#000000' };
        } else if (name === 'color' && edge && border[edge]) {
          const c = colourOf(attrs);
          if (c) border[edge]!.c = c;
        }
        return;
      }

      if (section === 'cellXfs') {
        if (name === 'xf') {
          xf = {
            numFmtId: Number(attrs.numFmtId ?? 0),
            fontId: Number(attrs.fontId ?? 0),
            fillId: Number(attrs.fillId ?? 0),
            borderId: Number(attrs.borderId ?? 0),
            style: {},
          };
        } else if (name === 'alignment' && xf) {
          const align = attrs.horizontal;
          if (align === 'left' || align === 'center' || align === 'right') xf.style.align = align;
          if (attrs.wrapText === '1' || attrs.wrapText === 'true') xf.style.overflow = 'wrap';
        }
      }
    },
    close(name) {
      if (section === 'fonts' && name === 'font') fonts.push(font);
      else if (section === 'fills' && name === 'fill') fills.push(fill);
      else if (section === 'fills' && name === 'patternFill') inPatternFill = false;
      else if (section === 'borders' && name === 'border') borders.push(border);
      else if (section === 'borders' && (name === 'left' || name === 'right' || name === 'top' || name === 'bottom')) {
        edge = null;
      } else if (section === 'cellXfs' && name === 'xf' && xf) {
        const built = { ...fonts[xf.fontId], ...xf.style };
        const bg = fills[xf.fillId];
        if (bg) built.bg = bg;
        const edges = borders[xf.borderId];
        if (edges && Object.keys(edges).length > 0) built.borders = { ...edges };
        numFmt.push(codes.get(xf.numFmtId) ?? BUILTIN_FORMATS[xf.numFmtId]);
        style.push(Object.keys(built).length > 0 ? built : undefined);
        xf = null;
      } else if (
        name === 'numFmts' || name === 'fonts' || name === 'fills' ||
        name === 'borders' || name === 'cellXfs' || name === 'cellStyleXfs' || name === 'dxfs'
      ) {
        section = '';
      }
    },
  });

  return { numFmt, style };
}

/**
 * `rgb="FFC00000"` is ARGB, and the alpha is dropped — gTables colours are CSS
 * hex. A theme or indexed colour is left UNSET rather than guessed: resolving
 * one means reading theme1.xml and applying a tint, and a wrong colour on a
 * heading row is more noticeable than no colour at all.
 */
function colourOf(attrs: Attrs): string | undefined {
  const rgb = attrs.rgb;
  if (!rgb) return undefined;
  const hex = rgb.length === 8 ? rgb.slice(2) : rgb;
  return /^[0-9a-fA-F]{6}$/.test(hex) ? `#${hex.toLowerCase()}` : undefined;
}

function borderWidth(style: string): number {
  switch (style) {
    case 'thick':
    case 'double':
      return 1.5;
    case 'medium':
    case 'mediumDashed':
    case 'mediumDashDot':
    case 'mediumDashDotDot':
      return 1;
    default:
      return 0.5;
  }
}

// ---------------------------------------------------------------------------
// Worksheets
// ---------------------------------------------------------------------------

/** Excel column widths are in characters; the grid wants pixels. */
function widthToPx(chars: number): number {
  return Math.max(24, Math.round(chars * 7 + 5));
}

/** Row heights are in points. */
function pointsToPx(points: number): number {
  return Math.max(12, Math.round((points * 4) / 3));
}

/**
 * A `<col>` run can legitimately say `max="16384"` — that is Excel's way of
 * writing "and all the rest". Materialising 16,384 widths for it would be a
 * pointless map the size of the sheet, so a run is only applied as far as any
 * sheet is plausibly wide.
 */
const MAX_COL_RUN = 1024;

function parseWorksheet(
  xml: string,
  name: string,
  hidden: boolean,
  strings: readonly string[],
  styles: Styles
): XlsxSheet {
  const sheet: XlsxSheet = {
    name,
    hidden,
    cells: [],
    colWidths: {},
    rowHeights: {},
    hiddenRows: {},
    hiddenCols: {},
    freezeRows: 0,
    freezeCols: 0,
    merged: 0,
    merges: [],
    rows: 0,
    cols: 0,
  };

  /** Shared formulas: the master text and where it was written, by `si`. */
  const shared = new Map<string, { formula: string; row: number; col: number }>();

  let row = 0;
  let col = 0;
  let cellType = 'n';
  let styleIndex = -1;
  let value = '';
  let formula = '';
  let formulaAttrs: Attrs = {};
  let capture: 'v' | 'f' | 't' | null = null;
  let inIs = false;
  let inCell = false;

  scanXml(xml, {
    open(tag, attrs) {
      switch (tag) {
        case 'pane':
          // A split that is not frozen scrolls independently; it is a view, not
          // a pinned header, and gTables has nothing to say about it.
          if (attrs.state === 'frozen' || attrs.state === 'frozenSplit') {
            sheet.freezeCols = Math.max(0, Math.round(Number(attrs.xSplit ?? 0)) || 0);
            sheet.freezeRows = Math.max(0, Math.round(Number(attrs.ySplit ?? 0)) || 0);
          }
          return;
        case 'col': {
          const min = Number(attrs.min ?? 0);
          const max = Math.min(Number(attrs.max ?? min), MAX_COL_RUN);
          if (!min) return;
          const custom = attrs.customWidth === '1' || attrs.customWidth === 'true';
          const isHidden = attrs.hidden === '1' || attrs.hidden === 'true';
          const width = Number(attrs.width);
          for (let c = min; c <= max; c++) {
            if (custom && Number.isFinite(width)) sheet.colWidths[c] = widthToPx(width);
            if (isHidden) sheet.hiddenCols[c] = true;
          }
          return;
        }
        case 'row': {
          row = Number(attrs.r ?? row + 1) || row + 1;
          const height = Number(attrs.ht);
          if ((attrs.customHeight === '1' || attrs.customHeight === 'true') && Number.isFinite(height)) {
            sheet.rowHeights[row] = pointsToPx(height);
          }
          if (attrs.hidden === '1' || attrs.hidden === 'true') sheet.hiddenRows[row] = true;
          col = 0;
          return;
        }
        case 'c': {
          const at = attrs.r ? parseCellA1(attrs.r) : null;
          // A `<c>` without an address is positional — the next column along.
          row = at?.row ?? row;
          col = at?.col ?? col + 1;
          cellType = attrs.t ?? 'n';
          styleIndex = attrs.s === undefined ? -1 : Number(attrs.s);
          value = '';
          formula = '';
          formulaAttrs = {};
          inCell = true;
          return;
        }
        case 'v':
          if (inCell) capture = 'v';
          return;
        case 'f':
          if (inCell) {
            capture = 'f';
            formulaAttrs = attrs;
          }
          return;
        case 'is':
          inIs = true;
          return;
        case 't':
          if (inIs) capture = 't';
          return;
        case 'mergeCell': {
          sheet.merged++;
          const range = attrs.ref ? parseRange(attrs.ref) : null;
          if (range) sheet.merges.push({ r1: range.r1, c1: range.c1, r2: range.r2, c2: range.c2 });
          return;
        }
      }
    },
    close(tag) {
      if (tag === 'v' || tag === 'f' || tag === 't') capture = null;
      else if (tag === 'is') inIs = false;
      else if (tag === 'c' && inCell) {
        inCell = false;
        finishCell();
      }
    },
    text(text) {
      if (capture === 'v') value += text;
      else if (capture === 'f') formula += text;
      else if (capture === 't') value += text;
    },
  });

  function finishCell(): void {
    const f = resolveFormula();
    const v = readValue();
    const numFmt = styleIndex >= 0 ? styles.numFmt[styleIndex] : undefined;
    const style = styleIndex >= 0 ? styles.style[styleIndex] : undefined;
    // A cell that is blank AND plain carries nothing; a blank cell wearing a
    // format or a fill is a deliberately empty row in a table, and dropping it
    // is what turns a banded schedule into a wall of white.
    if (v === null && f === undefined && numFmt === undefined && style === undefined) return;
    const cell: XlsxCell = { row, col, value: v };
    if (f !== undefined) cell.formula = f;
    if (numFmt !== undefined) cell.numFmt = numFmt;
    if (style !== undefined) cell.style = style;
    sheet.cells.push(cell);
    if (row > sheet.rows) sheet.rows = row;
    if (col > sheet.cols) sheet.cols = col;
  }

  function resolveFormula(): string | undefined {
    const kind = formulaAttrs.t;
    const si = formulaAttrs.si;
    if (kind === 'shared' && si !== undefined) {
      if (formula !== '') {
        // The master copy. Every other cell in the run refers back to it by
        // `si` and carries no text of its own.
        shared.set(si, { formula, row, col });
        return formula;
      }
      const master = shared.get(si);
      if (!master) return undefined;
      return translateShared(master.formula, row - master.row, col - master.col);
    }
    return formula === '' ? undefined : formula;
  }

  function readValue(): CellValue {
    if (value === '') return null;
    switch (cellType) {
      case 's': {
        const i = Number(value);
        return strings[i] ?? '';
      }
      case 'inlineStr':
      case 'str':
        return unescapeControlCodes(value);
      case 'b':
        return value === '1' || value.toLowerCase() === 'true';
      case 'e':
        // An error Excel had stored. Kept as the text it is: gTables has no
        // error VALUE, and "#DIV/0!" in the cell is at least honest.
        return value;
      case 'd':
        return value;
      default: {
        const n = Number(value);
        return Number.isFinite(n) ? n : value;
      }
    }
  }

  return sheet;
}

/**
 * Excel does not repeat a formula written down a column: it stores it once and
 * says "the other forty cells are this, moved". Expanding the run is the only
 * way those cells arrive as anything at all.
 *
 * Uses the app's own reference translator — the one behind fill-down — so a
 * shared formula lands exactly where dragging it here would have put it.
 */
function translateShared(formula: string, dRow: number, dCol: number): string {
  return translateFormula(formula, dRow, dCol);
}
